/* Cart drawer offers — one-tap discount codes.
 *
 * Shopify has no AJAX endpoint for applying a discount code, so this uses the
 * behaviour the storefront already exposes, verified against the live store:
 *
 *   apply   GET  /discount/<CODE>          sets the discount cookie; /cart.js
 *                                          reflects the new totals immediately
 *   remove  POST /cart/update.js {discount:""}
 *
 * Three things that are easy to get wrong and are handled below:
 *
 *   1. Applying a code while another is active silently fails — the first code
 *      stays. A swap must clear before it applies.
 *   2. An invalid or ineligible code still returns HTTP 200 and changes
 *      nothing, so success has to be confirmed by re-reading the cart.
 *   3. Codes surface at two levels: cart-wide (FIRSTCOFFEE) and per line item
 *      (BOGO). Only checking cart_level_discount_applications makes a working
 *      BOGO look like a failure.
 */
(function () {
  'use strict';

  var root = function () {
    var r = (window.routes && window.routes.root) || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.endsWith('/') ? r : r + '/';
  };

  function getCart() {
    return fetch(root() + 'cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); });
  }

  /* every discount title currently on the cart, lowercased */
  function appliedCodes(cart) {
    var out = [];
    (cart.cart_level_discount_applications || []).forEach(function (d) {
      if (d && d.title) out.push(String(d.title).toLowerCase().trim());
    });
    (cart.items || []).forEach(function (item) {
      (item.discounts || []).forEach(function (d) {
        if (d && d.title) out.push(String(d.title).toLowerCase().trim());
      });
      (item.line_level_discount_allocations || []).forEach(function (a) {
        var t = a && a.discount_application && a.discount_application.title;
        if (t) out.push(String(t).toLowerCase().trim());
      });
    });
    return out;
  }

  function clearDiscount() {
    return fetch(root() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' })
    }).then(function (r) { return r.json(); });
  }

  function applyDiscount(code) {
    return fetch(root() + 'discount/' + encodeURIComponent(code), {
      credentials: 'same-origin',
      cache: 'no-store'
    });
  }

  /* keep the cart page's coupon field from re-applying a stale code */
  function rememberCode(code) {
    try {
      if (code) localStorage.setItem('storedDiscount', code);
      else localStorage.removeItem('storedDiscount');
    } catch (e) { /* private mode — not worth failing the apply over */ }
  }

  function redrawDrawer(cart) {
    if (window.sharedFunctions && typeof window.sharedFunctions.updateSidebarCart === 'function') {
      window.sharedFunctions.updateSidebarCart(cart);
    } else {
      document.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));
    }
  }

  function setBusy(ticket, busy) {
    if (!ticket) return;
    ticket.classList.toggle('is-busy', !!busy);
    var label = ticket.querySelector('[data-cart-offer-label]');
    if (label && busy) {
      label.dataset.was = label.dataset.was || label.textContent;
      label.textContent = '…';
    }
  }

  function showError(ticket, message) {
    if (!ticket) return;
    var hint = ticket.querySelector('[data-cart-offer-hint]');
    if (hint) {
      hint.textContent = message;
      hint.classList.add('is-error');
    }
    var label = ticket.querySelector('[data-cart-offer-label]');
    if (label && label.dataset.was) label.textContent = label.dataset.was;
    ticket.classList.remove('is-busy');
  }

  function handle(button) {
    var code = button.getAttribute('data-cart-offer-apply');
    var isApplied = button.getAttribute('data-applied') === 'true';
    var ticket = button.closest('[data-cart-offer]');
    if (!code) return;

    setBusy(ticket, true);

    if (isApplied) {
      clearDiscount()
        .then(function () { rememberCode(null); return getCart(); })
        .then(redrawDrawer)
        .catch(function () { showError(ticket, 'Could not remove that code. Please try again.'); });
      return;
    }

    /* clear first — applying over a live code is a silent no-op */
    clearDiscount()
      .then(function () { return applyDiscount(code); })
      .then(function () { return getCart(); })
      .then(function (cart) {
        var landed = appliedCodes(cart).indexOf(code.toLowerCase().trim()) !== -1;
        if (!landed) {
          rememberCode(null);
          showError(ticket, 'This code does not apply to your cart yet.');
          return;
        }
        rememberCode(code);
        redrawDrawer(cart);
      })
      .catch(function () { showError(ticket, 'Could not apply that code. Please try again.'); });
  }

  /* ---- slider: dots, counter, scroll sync ---- */
  function initSlider(scope) {
    var wrap = (scope || document).querySelector('[data-cart-offers]');
    if (!wrap) return;
    var track = wrap.querySelector('[data-cart-offers-track]');
    var dots = wrap.querySelector('[data-cart-offers-dots]');
    var counter = wrap.querySelector('[data-cart-offers-counter]');
    if (!track) return;

    var slides = track.querySelectorAll('.cartOffers__slide');
    if (slides.length < 2) return;

    if (dots && !dots.children.length) {
      for (var i = 0; i < slides.length; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'cartOffers__dot';
        b.setAttribute('data-cart-offers-dot', String(i));
        b.setAttribute('aria-label', 'Offer ' + (i + 1));
        b.setAttribute('aria-current', i === 0 ? 'true' : 'false');
        dots.appendChild(b);
      }
    }

    function current() {
      var first = slides[0];
      var best = 0, min = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var d = Math.abs((slides[i].offsetLeft - first.offsetLeft) - track.scrollLeft);
        if (d < min) { min = d; best = i; }
      }
      return best;
    }

    function sync() {
      var idx = current();
      if (counter) counter.textContent = (idx + 1) + ' of ' + slides.length;
      if (dots) {
        for (var i = 0; i < dots.children.length; i++) {
          dots.children[i].setAttribute('aria-current', i === idx ? 'true' : 'false');
        }
      }
    }

    var t;
    track.addEventListener('scroll', function () {
      clearTimeout(t);
      t = setTimeout(sync, 60);
    }, { passive: true });

    if (dots) {
      dots.addEventListener('click', function (e) {
        var dot = e.target.closest('[data-cart-offers-dot]');
        if (!dot) return;
        var idx = parseInt(dot.getAttribute('data-cart-offers-dot'), 10) || 0;
        track.scrollTo({ left: slides[idx].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
      });
    }

    sync();
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cart-offer-apply]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    handle(btn);
  });

  /* the drawer is re-rendered by innerHTML swap, so re-init each time */
  document.addEventListener('cart-update', function () { initSlider(document); });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initSlider(document); });
  } else {
    initSlider(document);
  }
})();
